import { useCallback, useRef, useState } from 'react';

export type DoodleTool = 'brush' | 'eraser' | 'fill';

export interface DoodleStroke {
	points: number[][];
	color: string;
	size: number;
	tool: DoodleTool;
}

export const DOODLE_COLORS = [
	{ hex: '#1a1a1a', name: 'black' },
	{ hex: '#e03131', name: 'red' },
	{ hex: '#2f9e44', name: 'green' },
	{ hex: '#1971c2', name: 'blue' },
	{ hex: '#f08c00', name: 'orange' },
	{ hex: '#9c36b5', name: 'purple' },
	{ hex: '#0c8599', name: 'teal' },
	{ hex: '#e8590c', name: 'coral' },
] as const;

export const DOODLE_SIZES = [
	{ value: 1, dot: 3, name: 'extra_small' },
	{ value: 2, dot: 5, name: 'small' },
	{ value: 4, dot: 8, name: 'medium' },
	{ value: 8, dot: 12, name: 'large' },
	{ value: 14, dot: 16, name: 'extra_large' },
] as const;

interface StrokeHistory {
	strokes: DoodleStroke[];
	redoStack: DoodleStroke[];
}

export const useDoodle = () => {
	const [tool, setTool] = useState<DoodleTool>('brush');
	const [color, setColor] = useState<string>(DOODLE_COLORS[0].hex);
	const [size, setSize] = useState<number>(DOODLE_SIZES[1].value);
	const [history, setHistory] = useState<StrokeHistory>({ strokes: [], redoStack: [] });
	const [currentStroke, setCurrentStroke] = useState<DoodleStroke | null>(null);
	const currentStrokeRef = useRef<DoodleStroke | null>(null);

	const startStroke = useCallback(
		(point: number[]) => {
			setHistory((h) => ({ strokes: h.strokes, redoStack: [] }));
			const stroke: DoodleStroke = { points: [point], color, size, tool };
			currentStrokeRef.current = stroke;
			setCurrentStroke(stroke);
		},
		[color, size, tool]
	);

	const addPoint = useCallback((point: number[]) => {
		const prev = currentStrokeRef.current;
		if (!prev) return;
		const updated = { ...prev, points: [...prev.points, point] };
		currentStrokeRef.current = updated;
		setCurrentStroke(updated);
	}, []);

	const endStroke = useCallback(() => {
		const completed = currentStrokeRef.current;
		currentStrokeRef.current = null;
		setCurrentStroke(null);
		if (completed) {
			setHistory((h) => ({ strokes: [...h.strokes, completed], redoStack: [] }));
		}
	}, []);

	const performFill = useCallback(
		(point: number[]) => {
			const fillStroke: DoodleStroke = { points: [point], color, size: 0, tool: 'fill' };
			setHistory((h) => ({ strokes: [...h.strokes, fillStroke], redoStack: [] }));
		},
		[color]
	);

	const undo = useCallback(() => {
		setHistory((h) => {
			if (h.strokes.length === 0) return h;
			const removed = h.strokes[h.strokes.length - 1];
			return { strokes: h.strokes.slice(0, -1), redoStack: [...h.redoStack, removed] };
		});
	}, []);

	const redo = useCallback(() => {
		setHistory((h) => {
			if (h.redoStack.length === 0) return h;
			const restored = h.redoStack[h.redoStack.length - 1];
			return { strokes: [...h.strokes, restored], redoStack: h.redoStack.slice(0, -1) };
		});
	}, []);

	const clear = useCallback(() => {
		currentStrokeRef.current = null;
		setHistory({ strokes: [], redoStack: [] });
		setCurrentStroke(null);
	}, []);

	const { strokes, redoStack } = history;
	const canUndo = strokes.length > 0;
	const canRedo = redoStack.length > 0;
	const hasContent = strokes.length > 0 || currentStroke !== null;

	return {
		tool,
		setTool,
		color,
		setColor,
		size,
		setSize,
		strokes,
		currentStroke,
		startStroke,
		addPoint,
		endStroke,
		performFill,
		undo,
		redo,
		clear,
		canUndo,
		canRedo,
		hasContent,
	};
};
